/**
 * Tabela de itens da fila financeira PGMQ
 * Com filtros por status, tenant, event_type e paginação
 */

import { useState, Fragment, useMemo, useEffect } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, FileJson, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFinancialQueueItems, FinancialQueueItem } from '@/hooks/useFinancialQueueMonitor';
import { supabase } from '@/integrations/supabase/client';
import { PaginationFooter } from '@/components/ui/pagination-footer';

// Mapeamento de labels de status
const statusLabels: Record<string, string> = {
  pending: 'Pendente', processing: 'Processando', archived: 'Arquivado', dlq: 'DLQ',
};
const statusVariants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  pending: 'outline', processing: 'secondary', archived: 'default', dlq: 'destructive',
};

/** Formata tempo de processamento */
function formatProcessingTime(seconds: number | undefined): string {
  if (seconds === undefined || seconds === null) return '-';
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

/** Linha expansível para item da fila financeira */
function FinancialQueueItemRow({ item }: { item: FinancialQueueItem }) {
  const [isOpen, setIsOpen] = useState(false);
  const hasPayload = item.message && Object.keys(item.message).length > 0;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild>
      <Fragment>
        <TableRow className={cn('hover:bg-muted/30 cursor-pointer', isOpen && 'bg-muted/20')}>
          <TableCell className="font-mono text-xs">{item.msg_id}</TableCell>
          <TableCell><Badge variant={statusVariants[item.status] || 'outline'}>{statusLabels[item.status] || item.status}</Badge></TableCell>
          <TableCell><Badge variant="outline" className="font-mono">{item.event_type || '-'}</Badge></TableCell>
          <TableCell className="text-xs text-muted-foreground">{item.provider_slug || '-'}</TableCell>
          <TableCell className="font-medium">{item.tenant_name}</TableCell>
          <TableCell className="text-center">
            <span className={item.read_ct >= 3 ? 'text-red-600 font-bold' : ''}>{item.read_ct}/3</span>
          </TableCell>
          <TableCell className="text-sm text-muted-foreground">
            {formatDistanceToNow(new Date(item.enqueued_at), { addSuffix: true, locale: ptBR })}
          </TableCell>
          <TableCell className="text-sm font-mono">{formatProcessingTime(item.processing_time)}</TableCell>
          <TableCell>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
          </TableCell>
        </TableRow>
        <CollapsibleContent asChild>
          <TableRow className="bg-muted/10 hover:bg-muted/10">
            <TableCell colSpan={9} className="p-0">
              <div className="p-4 space-y-4 border-t border-border/50">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium"><FileJson className="h-4 w-4 text-primary" /> Payload</div>
                  {hasPayload ? (
                    <pre className="text-xs bg-muted/50 p-3 rounded-md overflow-x-auto text-muted-foreground max-h-[300px] overflow-y-auto">
                      {JSON.stringify(item.message, null, 2)}
                    </pre>
                  ) : <p className="text-sm text-muted-foreground">Sem payload</p>}
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" /> Enfileirado: {format(new Date(item.enqueued_at), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}
                  </span>
                  {item.vt && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" /> VT: {format(new Date(item.vt), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}
                    </span>
                  )}
                </div>
              </div>
            </TableCell>
          </TableRow>
        </CollapsibleContent>
      </Fragment>
    </Collapsible>
  );
}

export function FinancialQueueItemsTable() {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [tenantFilter, setTenantFilter] = useState<string>('all');
  const [attemptsFilter, setAttemptsFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data: items, isLoading } = useFinancialQueueItems(statusFilter, 200);

  // Buscar lista de tenants para dropdown
  const { data: tenants = [] } = useQuery({
    queryKey: ['tenants-list-financial'],
    queryFn: async () => {
      const { data } = await supabase.from('tenants').select('id, name').order('name');
      return data || [];
    },
  });

  // Filtragem e paginação no frontend
  const { paginatedItems, totalFiltered } = useMemo(() => {
    if (!items) return { paginatedItems: [], totalFiltered: 0 };
    let filtered = items;

    if (tenantFilter !== 'all') filtered = filtered.filter(i => i.tenant_id === tenantFilter);
    if (attemptsFilter !== 'all') {
      if (attemptsFilter === '3+') filtered = filtered.filter(i => i.read_ct >= 3);
      else filtered = filtered.filter(i => i.read_ct === parseInt(attemptsFilter));
    }

    const startIndex = (currentPage - 1) * pageSize;
    return { paginatedItems: filtered.slice(startIndex, startIndex + pageSize), totalFiltered: filtered.length };
  }, [items, tenantFilter, attemptsFilter, currentPage, pageSize]);

  useEffect(() => { setCurrentPage(1); }, [statusFilter, tenantFilter, attemptsFilter]);

  if (isLoading) {
    return <Card><CardHeader><CardTitle>Itens da Fila</CardTitle></CardHeader><CardContent><Skeleton className="h-[400px] w-full" /></CardContent></Card>;
  }

  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2">📋 Itens da Fila</CardTitle></CardHeader>
      <CardContent>
        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos Status</SelectItem>
              <SelectItem value="pending">Pendentes</SelectItem>
              <SelectItem value="processing">Processando</SelectItem>
              <SelectItem value="archived">Arquivados</SelectItem>
              <SelectItem value="dlq">DLQ</SelectItem>
            </SelectContent>
          </Select>

          <Select value={tenantFilter} onValueChange={setTenantFilter}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Tenant" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos Tenants</SelectItem>
              {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={attemptsFilter} onValueChange={setAttemptsFilter}>
            <SelectTrigger className="w-[140px]"><SelectValue placeholder="Tentativas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="0">0 tentativas</SelectItem>
              <SelectItem value="1">1 tentativa</SelectItem>
              <SelectItem value="2">2 tentativas</SelectItem>
              <SelectItem value="3+">3+ tentativas</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tabela */}
        {!paginatedItems || paginatedItems.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">Nenhum item encontrado na fila.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[70px]">ID</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead className="w-[120px]">Evento</TableHead>
                  <TableHead className="w-[100px]">Provider</TableHead>
                  <TableHead>Tenant</TableHead>
                  <TableHead className="w-[80px]">Tentativas</TableHead>
                  <TableHead className="w-[100px]">Criado</TableHead>
                  <TableHead className="w-[70px]">Tempo</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedItems.map(item => (
                  <FinancialQueueItemRow key={`${item.source}-${item.msg_id}`} item={item} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Paginação */}
        {totalFiltered > pageSize && (
          <div className="mt-4">
            <PaginationFooter
              currentPage={currentPage}
              pageSize={pageSize}
              totalItems={totalFiltered}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
